import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactTable from "react-table";
import { format } from "date-fns";
import ReactTablePagination from "components/task-list/components/pagination";
import { DATE_FNS_ISO_DATE_FORMAT } from "shared/constants";
import { getPagingParams, usePagination } from "shared/hooks/use-pagination";
import { useSearch } from "../../shared/hooks/api/use-search";
import { searchSLA } from "../clients/lib/data";
import { useUpdateSla } from "../../shared/hooks/api/use-update-sla";
import { buildSlaGroups } from "./lib/constants";
import { getStorageValue, setStorageValue } from "../../shared/storage";
import { useExportCsv } from "../../shared/hooks/api/use-export-csv";
import * as notify from "../../shared/notify";

const filtersStorageKey = "sla-groups-filters";
const pagingStorageKey = "sla-groups-paging-params";
const defaultPagingParams = {
    pageSize: 10,
    sortBy: [
        {
            id: "shortName",
            desc: false,
        },
    ],
};

const SlaGroups = () => {
    const formattedDate = format(new Date(), DATE_FNS_ISO_DATE_FORMAT);
    const storedPagingParams = getStorageValue(pagingStorageKey);
    const storedSearchParams = getStorageValue(filtersStorageKey);
    const [pagingDisabled, setPagingDisabled] = useState(false);
    const [
        { pageNumber, pageSize, sortBy, totalPages, searchParams, pagingParams, filteredValues },
        { setPageSize, setPage, setSortBy, setTotalCount, search },
    ] = usePagination({
        defaultValues: storedPagingParams ? getPagingParams(storedPagingParams) : defaultPagingParams,
        defaultFilters: storedSearchParams || [],
    });
    const { data: searchResult, mutateAsync: searchProducts, isLoading } = useSearch(null, false, searchSLA);
    const { mutateAsync: updateSla, isLoading: isUpdatingSla } = useUpdateSla();
    const { refetch: exportCsv } = useExportCsv("/sla/clients/export", { fileName: `Client_SLA_Final_${formattedDate}.csv` });
    const isEmpty = searchResult?.items?.length === 0;

    const updateCell = useCallback(
        async (rowData, field) => {
            const result = await updateSla({
                id: rowData.id,
                shortName: field.id === "shortName" ? field.value : rowData.shortName,
                description: field.id === "description" ? field.value : rowData.description,
            });

            if (result) {
                notify.success(`${field.id === "shortName" ? "Group Name" : "Description"} updated successfully`);
            }

            if (searchParams && pagingParams) {
                searchProducts({ searchParams, pagingParams });
            }
        },
        [pagingParams, searchParams, searchProducts, updateSla]
    );

    const columns = useMemo(() => {
        return buildSlaGroups(updateCell, pagingDisabled || isUpdatingSla, setPagingDisabled);
    }, [isUpdatingSla, pagingDisabled, updateCell]);

    const performSearch = useCallback(
        async (searchProperties, pagingProperties) => {
            const result = await searchProducts({ searchParams: searchProperties, pagingParams: pagingProperties });

            if (result) {
                setTotalCount(result.totalRecordCount);
            }
        },
        [searchProducts, setTotalCount]
    );

    useEffect(() => {
        if (searchParams && pagingParams) {
            performSearch(searchParams, pagingParams);
            setStorageValue(filtersStorageKey, searchParams);
            setStorageValue(pagingStorageKey, pagingParams);
        }
    }, [searchParams, pagingParams, performSearch]);

    const handleExportSLAs = async (e) => {
        e.preventDefault();

        const { data: file } = await exportCsv();

        if (!file) {
            return notify.error("You do not have permission to perform this action.");
        }

        return file;
    };

    return (
        <div className="container">
            <div className="m-b-12">
                <div className="flex justify-between">
                    <h2 className="txt-nowrap m-b-12">SLA Groups</h2>
                    <div className="button-group justify-content-end">
                        <button
                            className="button -no-margin"
                            onClick={handleExportSLAs}
                        >
                            BULK EXPORT SLAs
                        </button>
                    </div>
                </div>
                <div className="flex">
                    <span>View or update Group Names and Descriptions in the table.</span>
                </div>
            </div>
            <ReactTable
                loading={isLoading || isUpdatingSla}
                manual
                data={searchResult?.items || []}
                filtered={filteredValues}
                columns={columns}
                multiSort
                pagingDisabled={pagingDisabled || isUpdatingSla}
                minRows={searchResult?.items?.length > 0 ? 1 : 3}
                totalRecords={searchResult?.totalRecordCount}
                PaginationComponent={ReactTablePagination}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                onSortedChange={setSortBy}
                onFilteredChange={search}
                sorted={sortBy}
                page={pageNumber}
                pageSize={pageSize}
                pages={totalPages}
                sortable={!pagingDisabled && !isUpdatingSla && !isEmpty}
            />
        </div>
    );
};

export default SlaGroups;
